import * as dat from 'lil-gui'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import firefliesVertexShader from './shaders/fireflies/vertex.glsl'
import firefliesFragmentShader from './shaders/fireflies/fragment.glsl'
import portalVertexShader from './shaders/portal/vertex.glsl'
import portalFragmentShader from './shaders/portal/fragment.glsl'

/**
 * Base
 */
// Debug
  const debugObject = {}
const gui = new dat.GUI({
    width: 400
})

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Loaders
 */
// Texture loader
const textureLoader = new THREE.TextureLoader()

// Draco loader
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('draco/')

// GLTF loader
const gltfLoader = new GLTFLoader()
gltfLoader.setDRACOLoader(dracoLoader)

/**
 * Texture
 */
const bakedTexture = textureLoader.load('baked2.jpg')
bakedTexture.flipY = false
bakedTexture.encoding = THREE.sRGBEncoding
console.log(bakedTexture)
/**
 * Materials
 */
// Baked material
const bakedMaterial = new THREE.MeshBasicMaterial({
  // color: '#FFF885',
  map: bakedTexture,
})

// Pole light material
const poleLightMaterial = new THREE.MeshBasicMaterial({color: '#FFF885'})
const portalLightMaterial = new THREE.ShaderMaterial({
  vertexShader: portalVertexShader,
  fragmentShader: portalFragmentShader,
  uniforms: {
    uTime: {value: 0},
    uColorOne: { value: new THREE.Color('#0a1f0c') },
    uColorTwo: { value: new THREE.Color('#00d9ff') },
    uColorThree: { value: new THREE.Color('#eafdff') },
  },
})

/**
 * Models
 */
gltfLoader.load(
  'portal -m.glb',
  (gltf) => {
    console.log(gltf.scene.children)
    const bakedMesh = gltf.scene.children.find((child) => child.name === 'bakedGeo')
    bakedMesh.material = bakedMaterial
    
    const portalLightMesh = gltf.scene.children.find((child) => child.name === 'PortalFace')
    portalLightMesh.material = portalLightMaterial
    const poleLightA = gltf.scene.children.find((child) => child.name === 'LightBodyA')
    poleLightA.material = poleLightMaterial
    const poleLightB = gltf.scene.children.find((child) => child.name === 'LightBodyB')
    poleLightB.material = poleLightMaterial
    gltf.scene.rotateY(Math.PI)
    scene.add(gltf.scene)
  }
)

/**
 * Fireflies
 */
// Geometry
const firefliesGeometry = new THREE.BufferGeometry()
const firefliesCount = 60;
const positionArray = new Float32Array(firefliesCount * 3).map((v, index, a) => {
  switch (index % 3) {
    case 0: return (Math.random() - 0.5) * 5;
    case 1: return Math.random() * 2.5;
    case 2: return (Math.random() - 0.5) * 5;
  }
})
const scaleArray = new Float32Array(firefliesCount).map(() => Math.random())

firefliesGeometry.setAttribute('position', new THREE.BufferAttribute(positionArray, 3))
firefliesGeometry.setAttribute('aScale', new THREE.BufferAttribute(scaleArray, 1))
// Material
const firefliesMaterial = new THREE.ShaderMaterial({
  uniforms: {
    uPixelRatio: {value: Math.min(window.devicePixelRatio, 2)},
    uSize: {value: 240},
    uTime: {value: 0},
  },
  vertexShader: firefliesVertexShader,
  fragmentShader: firefliesFragmentShader,
  depthWrite: false,
  blending: THREE.AdditiveBlending,
  transparent: true,
})

gui.add(firefliesMaterial.uniforms.uSize, 'value').min(0).max(500).step(1).name('fireflies size')

// Points
const fireflies = new THREE.Points(firefliesGeometry, firefliesMaterial)
scene.add(fireflies)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  
    // Update fireflies
    firefliesMaterial.uniforms.uPixelRatio.value = Math.min(window.devicePixelRatio, 2)
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(45, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 4
camera.position.y = 2
camera.position.z = 4
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.dampingFactor = 0.1;
controls.enablePan = false;
controls.enableZoom = false;
// controls.maxAzimuthAngle = Math.PI/3;
// controls.minAzimuthAngle = -Math.PI/3;
controls.maxPolarAngle = Math.PI * 0.5 - 0.1;
// controls.minPolarAngle = Math.PI * 1/4;
controls.update();
// orbitControls.enabled = false;


/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.outputEncoding = THREE.sRGBEncoding

debugObject.clearColor = '#0a1f0c';
renderer.setClearColor (debugObject.clearColor);
gui
  .addColor(debugObject, 'clearColor')
  .onChange(() => {
    renderer.setClearColor (debugObject.clearColor)
  })

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
  
  firefliesMaterial.uniforms.uTime.value = elapsedTime;
  portalLightMaterial.uniforms.uTime.value = elapsedTime;
}

tick()